import { FC, useCallback } from 'react'
import { toast } from 'react-toastify'

import { strapi } from 'api'
import { SubscribeData } from 'api/strapi/newsletters'
import { Form, useForm } from 'components/form'
import {
  CheckboxFormField,
  EMAIL_RULE,
  EmailFormField,
  FIRST_NAME_RULE,
  FirstNameFormField,
  LAST_NAME_RULE,
  LastNameFormField,
} from 'components/form-field'
import { RadioFormField, RadioFormFieldGroup } from 'components/form-field/RadioFormField'
import { yup } from 'components/form/yup'
import { Heading } from 'components/heading'
import { SubmitFormButton } from 'components/submit-form-button'
import { FormNameType } from 'utils/types'
import styles from 'sections/articles/newsletters/subscribe-form.module.css'
import { I_AM_TYPE_SELECT_OPTIONS } from 'const/i-am-options'

const FIELD: FormNameType<SubscribeData> = {
  email: 'email',
  first_name: 'first_name',
  last_name: 'last_name',
  type: 'type',
  agreed: 'agreed',
}

const FORM_VALIDATOR = yup.object().shape({
  [FIELD.email]: EMAIL_RULE,
  [FIELD.first_name]: FIRST_NAME_RULE,
  [FIELD.last_name]: LAST_NAME_RULE,
  [FIELD.agreed]: yup.boolean().required(),
  [FIELD.type]: yup.string().required(),
})

export interface NewslettersSubscribeFormProps {}
export const NewslettersSubscribeForm: FC<NewslettersSubscribeFormProps> = () => {
  const formMethods = useForm<SubscribeData>(FORM_VALIDATOR)
  const submit = useCallback(
    async (data) => {
      //TODO!: It would be good if a form caught errors without these wrappers
      try {
        const result = await strapi.newsletters.subscribe(data)
        toast.success(`Thanks for joining!`)
        formMethods.reset()
        return result
      } catch (error) {
        toast.error(error.message)
      }
    },
    [formMethods],
  )

  return (
    <div className={styles.container}>
      <Heading theme="h2" component="h2" className={styles.heading}>
        Get valuable articles and resources sent to your inbox.
      </Heading>
      <p className={styles.description}>
        Get the latest articles and insights delivered directly to your inbox. We send periodic
        e-mail updates highlighting the latest research, data and news impacting residential real
        estate, curated and vetted by our team of experts.
      </p>
      <Form formMethods={formMethods} onSubmit={submit} className={styles.form}>
        <FirstNameFormField
          name={FIELD.first_name}
          label="First name"
          className={styles.firstName}
        />
        <LastNameFormField name={FIELD.last_name} label="Last name" className={styles.lastName} />
        <EmailFormField name={FIELD.email} label="Email address" />
        <RadioFormFieldGroup fieldName={FIELD.type} groupTitle="I am a:" theme="pills">
          {I_AM_TYPE_SELECT_OPTIONS.map(({ value, label }) => (
            <RadioFormField
              value={value}
              key={value}
              theme="pill"
              label={label}
              className={styles.radioButton}
              classNameLabel={styles.radioButtonLabel}
            />
          ))}
        </RadioFormFieldGroup>
        <CheckboxFormField name={FIELD.agreed}>
          I consent to receive occasional email communications from REZITRADE and acknowledge that I
          can unsubscribe at any time.
        </CheckboxFormField>
        <SubmitFormButton theme="secondary" className={styles.submit}>
          Subscribe
        </SubmitFormButton>
      </Form>
    </div>
  )
}
